// components/skills/SkillsMonetizer.jsx
// Rebuilt: Honest token values, proper value framing
// ============================================
// This component shows users how they can earn tokens through local work.
// Key principle: Frame as "earn grocery credits" not "make $XX/hour"
// because tokens are platform credit, not cash.
// ============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Truck, 
  Users, 
  ClipboardCheck, 
  Package,
  Clock,
  MapPin,
  ChevronRight,
  Coins,
  TrendingUp,
  Info,
  Calculator
} from 'lucide-react';
import { 
  TOKEN_CONFIG, 
  formatTokens, 
  formatTokenValue,
  calculateSessionEarnings,
  getRegionalMultiplier 
} from '../../utils/tokenEconomy';

const SkillsMonetizer = () => {
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [hoursPerWeek, setHoursPerWeek] = useState(5);
  
  // Job types with HONEST token values from our token economy
  const jobTypes = [
    {
      id: 'local-delivery',
      title: 'Local Delivery',
      icon: Truck,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      description: 'Deliver group orders to neighbors in your area',
      requirements: [
        'Live in an active UniLedge zone',
        'Reliable transportation (car, bike, or walking)',
        'Smartphone for route navigation'
      ],
      tokenRange: {
        min: TOKEN_CONFIG.JOB_RATES.LOCAL_DELIVERY_SHORT,
        max: TOKEN_CONFIG.JOB_RATES.LOCAL_DELIVERY_LONG,
        perJob: true
      },
      timeRange: '15-45 min per delivery',
      distanceNote: 'Most deliveries are under 2 miles',
      bonuses: [
        { name: 'On-time completion', tokens: TOKEN_CONFIG.JOB_BONUSES.ON_TIME_COMPLETION },
        { name: '5-star rating', tokens: TOKEN_CONFIG.JOB_BONUSES.FIVE_STAR_RATING },
        { name: 'First job of day', tokens: TOKEN_CONFIG.JOB_BONUSES.FIRST_JOB_OF_DAY }
      ]
    },
    {
      id: 'group-coordinator',
      title: 'Group Coordinator',
      icon: Users,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      description: 'Help organize and manage group purchases in your community',
      requirements: [
        'Good communication skills',
        'Organized and reliable',
        'Trust score of 3.5+'
      ],
      tokenRange: {
        min: TOKEN_CONFIG.JOB_RATES.ROUTE_COORDINATION,
        max: TOKEN_CONFIG.JOB_RATES.ROUTE_COORDINATION,
        perJob: false,
        perHour: true
      },
      timeRange: '1-2 hours per coordination',
      distanceNote: 'Work from home',
      bonuses: [
        { name: 'Successful group completion', tokens: 30 },
        { name: 'Group reaches 10+ members', tokens: 50 }
      ]
    },
    {
      id: 'quality-check',
      title: 'Quality Checker',
      icon: ClipboardCheck,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      description: 'Verify order accuracy and product quality before delivery',
      requirements: [
        'Attention to detail',
        'Near a pickup location',
        'Trust score of 3.5+'
      ],
      tokenRange: {
        min: TOKEN_CONFIG.JOB_RATES.QUALITY_CHECK,
        max: TOKEN_CONFIG.JOB_RATES.QUALITY_CHECK,
        perJob: true
      },
      timeRange: '10-20 min per check',
      distanceNote: 'At pickup locations only',
      bonuses: [
        { name: 'Zero errors reported', tokens: 15 }
      ]
    },
    {
      id: 'inventory-assist',
      title: 'Inventory Assistant',
      icon: Package,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      description: 'Help vendors organize and prepare bulk orders',
      requirements: [
        'Physical ability to lift/move products',
        'Available during business hours',
        'Trust score of 4.0+'
      ],
      tokenRange: {
        min: TOKEN_CONFIG.JOB_RATES.INVENTORY_ASSIST,
        max: TOKEN_CONFIG.JOB_RATES.INVENTORY_ASSIST,
        perJob: false,
        perHour: true
      },
      timeRange: '2-4 hours per session',
      distanceNote: 'At vendor locations',
      bonuses: [
        { name: 'Shift completion', tokens: 20 },
        { name: 'Vendor positive review', tokens: 25 }
      ]
    }
  ];
  
  // Calculate weekly/monthly earnings estimate
  const calculateEarnings = (hours) => {
    const session = calculateSessionEarnings(hours, 'AVERAGE_COST');
    const weekly = session.estimatedTokens;
    const monthly = weekly * 4;
    
    return {
      weekly: {
        tokens: weekly,
        value: formatTokenValue(weekly)
      },
      monthly: {
        tokens: monthly,
        value: formatTokenValue(monthly)
      },
      // What that could cover
      couldCover: getSpendingExamples(monthly)
    };
  };
  
  // Show relatable spending examples
  const getSpendingExamples = (monthlyTokens) => {
    const value = monthlyTokens * TOKEN_CONFIG.VALUE_PER_TOKEN;
    
    if (value >= 150) {
      return "A month of household essentials";
    } else if (value >= 100) {
      return "Most of your cleaning supplies";
    } else if (value >= 50) {
      return "Paper products and toiletries";
    } else if (value >= 25) {
      return "Several household items";
    }
    return "A few essential items";
  };
  
  const earnings = calculateEarnings(hoursPerWeek);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-xl font-bold mb-2">Turn Spare Time Into Savings</h2>
        <p className="text-indigo-100 mb-4">
          Help your community and earn tokens you can use on household products.
        </p>
        
        {/* Key Value Props */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/10 rounded-lg p-3">
            <MapPin className="w-5 h-5 mx-auto mb-1" />
            <p className="text-xs">Local Only</p>
            <p className="text-xs text-indigo-200">Under 2 miles</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <Clock className="w-5 h-5 mx-auto mb-1" />
            <p className="text-xs">Flexible</p>
            <p className="text-xs text-indigo-200">Your schedule</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <Coins className="w-5 h-5 mx-auto mb-1" />
            <p className="text-xs">Earn Tokens</p>
            <p className="text-xs text-indigo-200">Save on products</p>
          </div>
        </div>
      </div>
      
      {/* How It Works - Important Context */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-800 mb-1">How Token Earning Works</h3>
            <p className="text-sm text-amber-700">
              Tokens are UniLedge platform credits (1 token = $0.10). You can redeem 
              up to 40% of your order value in tokens. This isn't a salary—it's a way 
              to earn savings while helping your community.
            </p>
          </div>
        </div>
      </div>
      
      {/* Earnings Calculator */}
      <div className="bg-gray-50 rounded-xl p-5">
        <button
          onClick={() => setShowCalculator(!showCalculator)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center">
            <Calculator className="w-5 h-5 text-gray-600 mr-2" />
            <span className="font-semibold text-gray-800">Estimate Your Earnings</span>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showCalculator ? 'rotate-90' : ''}`} />
        </button>
        
        {showCalculator && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {/* Hours Slider */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hours per week: <span className="text-indigo-600 font-bold">{hoursPerWeek}</span>
              </label>
              <input
                type="range"
                min="1"
                max="20"
                value={hoursPerWeek}
                onChange={(e) => setHoursPerWeek(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1 hr</span>
                <span>10 hrs</span>
                <span>20 hrs</span>
              </div>
            </div>
            
            {/* Earnings Display */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                <p className="text-sm text-gray-600 mb-1">Weekly Tokens</p>
                <p className="text-2xl font-bold text-indigo-600">{formatTokens(earnings.weekly.tokens)}</p>
                <p className="text-xs text-gray-500">{earnings.weekly.value} value</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                <p className="text-sm text-gray-600 mb-1">Monthly Tokens</p>
                <p className="text-2xl font-bold text-green-600">{formatTokens(earnings.monthly.tokens)}</p>
                <p className="text-xs text-gray-500">{earnings.monthly.value} value</p>
              </div>
            </div>
            
            {/* What It Could Cover */}
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-sm text-green-800">
                <span className="font-semibold">That could cover:</span> {earnings.couldCover}
              </p>
            </div>
            
            {/* Disclaimer */}
            <p className="text-xs text-gray-500 mt-3 text-center">
              *Estimates based on {TOKEN_CONFIG.BASE_HOURLY_TOKEN_RATE} tokens/hour average. 
              Actual earnings vary by job type, location, and bonuses.
            </p>
          </div>
        )}
      </div>
      
      {/* Job Types */}
      <div>
        <h3 className="font-semibold text-gray-800 mb-3">Ways to Earn</h3>
        <div className="space-y-3">
          {jobTypes.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              isExpanded={selectedJob === job.id}
              onToggle={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
            />
          ))}
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg mb-1">Ready to Start Earning?</h3>
            <p className="text-green-100 text-sm">
              Complete your profile and verify your location to see available jobs.
            </p>
          </div>
          <button
            onClick={() => navigate('/earn')}
            className="bg-white text-green-600 px-5 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors whitespace-nowrap"
          >
            View Jobs
          </button>
        </div>
      </div>
      
      {/* Trust Score Connection */}
      <div className="bg-gray-100 rounded-lg p-4">
        <div className="flex items-start">
          <TrendingUp className="w-5 h-5 text-gray-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Build Trust, Unlock More</h4>
            <p className="text-sm text-gray-600">
              Higher trust scores unlock better-paying jobs and priority access to new opportunities. 
              Start with basic tasks and grow your reputation.
            </p>
            <div className="flex gap-2 mt-2">
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">3.0+ Basic Jobs</span>
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">3.5+ Quality Check</span>
              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">4.0+ Inventory</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual Job Card Component
const JobCard = ({ job, isExpanded, onToggle }) => {
  const IconComponent = job.icon;
  
  // Format token display
  const tokenDisplay = job.tokenRange.perJob
    ? `${job.tokenRange.min}-${job.tokenRange.max} tokens/job`
    : `${job.tokenRange.min} tokens/hour`;
  
  const valueDisplay = job.tokenRange.perJob
    ? `${formatTokenValue(job.tokenRange.min)}-${formatTokenValue(job.tokenRange.max)}`
    : `${formatTokenValue(job.tokenRange.min)}/hr`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center">
          <div className={`w-12 h-12 ${job.iconBg} rounded-lg flex items-center justify-center mr-3`}>
            <IconComponent className={`w-6 h-6 ${job.iconColor}`} />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-gray-800">{job.title}</h4>
            <p className="text-sm text-gray-500">{job.description}</p>
          </div>
        </div>
        <div className="text-right ml-4">
          <p className="font-bold text-indigo-600">{tokenDisplay}</p>
          <p className="text-xs text-gray-500">{valueDisplay}</p>
        </div>
      </button>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Time & Distance */}
          <div className="flex gap-4 py-3 text-sm">
            <div className="flex items-center text-gray-600">
              <Clock className="w-4 h-4 mr-1" />
              {job.timeRange}
            </div>
            <div className="flex items-center text-gray-600">
              <MapPin className="w-4 h-4 mr-1" />
              {job.distanceNote}
            </div>
          </div>
          
          {/* Requirements */}
          <div className="mb-3">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Requirements:</h5>
            <ul className="space-y-1">
              {job.requirements.map((req, idx) => (
                <li key={idx} className="text-sm text-gray-600 flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  {req}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Bonuses */}
          {job.bonuses && job.bonuses.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-3">
              <h5 className="text-sm font-medium text-yellow-800 mb-2">Bonus Tokens:</h5>
              <div className="flex flex-wrap gap-2">
                {job.bonuses.map((bonus, idx) => (
                  <span 
                    key={idx}
                    className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full"
                  >
                    +{bonus.tokens} {bonus.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillsMonetizer;